<script lang="ts" setup>

import { useAlertsStore } from '@/stores/alerts/useAlertsStore';
import { debounce } from '@/utils/debounce';
import { ref } from '#imports';

type Props = {
    max?: number;
    size?: 'default' | 'medium';
    isLoading?: boolean;
    disabled?: boolean;
    min?: number;
    initialValue?: number;
};

const props = withDefaults(
    defineProps<Props>(),
    {
        size: 'default',
        max: undefined,
        isLoading: undefined,
        disabled: undefined,
        min: 1,
        initialValue: 1
    }
);

const inputValue = ref(props.initialValue);

const alertStore = useAlertsStore();

const emit = defineEmits<{
    (e: 'onChange', payload: number): void;
}>();

const onValueChange = (val: number) => {
    emit(
        'onChange',
        val
    );
    inputValue.value = val;
};

const onChangeCounter = (type: 'increment' | 'decrement') => {
    if (type === 'increment') {
        onValueChange(inputValue.value + 1);
    }
    else {
        onValueChange(inputValue.value - 1);
    }
};

const onChangeInputDebounce = debounce(
    (val) => {
        const raw = val?.target?.value || '';
        const cleaned = raw.replace(/\D/g, '');
        const num = Number(cleaned);

        if (isNaN(num) || num === 0) {
            onValueChange(props.min);
            return;
        }

        if (props?.max && Number(val.target.value) > props.max) {
            alertStore.add({
                critical: false,
                description: `You can buy no more than ${props.max} tokens at a time`,
                status: 'type.info',
                title: 'Warning'
            });
            onValueChange(props.max);
        }
        else if (Number(val.target.value) < props.min) {
            onValueChange(props.min);
        }
        else {
            onValueChange(num);
        }
    },
    500
);

watch(() => props.max, (maxVal) => {
    if (maxVal && maxVal > inputValue.value) {
        onValueChange(maxVal);
    }
});

</script>

<template>
  <div class="display-flex align-items-center justify-between counter"
       :class="`async-counter-${size}`"
       v-bind="$attrs"
  >
    <div :class="{ 'counter__btn--disabled': inputValue <= props.min || disabled || isLoading }"
         type="button"
         role="button"
         aria-label="Reduce the amount"
         title="Remove one"
         class="counter__btn display-flex align-items-center justify-center cursor-pointer"
         @click="onChangeCounter('decrement')"
    >
      <IconMain :size="17"
                icon="minus"
      />
    </div>
    <LoaderDots v-if="isLoading" />
    <input v-else
           :disabled="disabled"
           v-model="inputValue"
           aria-label="counter value"
           @input="onChangeInputDebounce"
    >
    <div :class="{ 'counter__btn--disabled': (!!props?.max && inputValue >= props.max) || disabled || isLoading }"
         type="button"
         role="button"
         aria-label="Increase the amount"
         title="Add one more"
         class="counter__btn display-flex align-items-center justify-center cursor-pointer"
         @click="onChangeCounter('increment')"
    >
      <IconMain :size="17"
                icon="plus"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>

.counter {
  overflow: hidden;

  button, input {
    background-color: transparent;
  }

  &__btn {
    transition: opacity 0.3s ease-in-out;

    &--disabled {
      opacity: 0.6;
      pointer-events: none;
    }

    &:hover {
      opacity: 0.8;
    }
  }

  input {
    width: auto;
    padding: 0;
    border: none;
    outline: none;
    color: var(--content-primary);
    font-family: var(--font), sans-serif;
    font-weight: 600;
    text-align: center;
  }
}

.async-counter {
  &-default {
    border-radius: 28px;
    background-color: var(--layer-floor-1);

    input {
      width: calc(100% - 56px);
      font-size: 14px;
    }

    .counter__btn {
      width: 28px;
      height: 28px;
    }
  }

  &-medium {
    border-radius: 72px;
    background-color: var(--bg-secondary);

    input {
      width: calc(100% - 144px);
      font-size: 18px;
    }

    .counter__btn {
      width: 72px;
      height: 72px;
    }
  }
}

</style>
